import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useEffect, useMemo } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { LegionError } from '../../utils/legion';
import { fireOperationalEvent } from '../analytics';
import { useLazyService } from '../use-service';
import { useTeamsPermissionsStore } from './teams-permission-store';
import { requestPermissions } from './utils';
var useTeamsPermissionsServiceLegacy = function useTeamsPermissionsServiceLegacy(scope) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      enabled: false,
      initialState: {},
      attributes: {}
    },
    enabled = _ref.enabled,
    initialState = _ref.initialState,
    attributes = _ref.attributes;
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;

  // backwards compatibility allow orgId to be passed as a string
  var _ref2 = typeof scope === 'string' || !scope ? {
      orgId: scope,
      cloudId: undefined
    } : scope,
    orgId = _ref2.orgId,
    cloudId = _ref2.cloudId;
  var _useTeamsPermissionsS = useTeamsPermissionsStore(),
    _useTeamsPermissionsS2 = _slicedToArray(_useTeamsPermissionsS, 2),
    state = _useTeamsPermissionsS2[0],
    _useTeamsPermissionsS3 = _useTeamsPermissionsS2[1],
    setError = _useTeamsPermissionsS3.setError,
    setLoading = _useTeamsPermissionsS3.setLoading,
    setPermissions = _useTeamsPermissionsS3.setPermissions;
  var _useMemo = useMemo(function () {
      return requestPermissions({
        orgId: orgId,
        apiVersion: 'v4',
        siteId: cloudId || 'None'
      });
    }, [orgId, cloudId]),
    _useMemo2 = _slicedToArray(_useMemo, 2),
    request = _useMemo2[0],
    controller = _useMemo2[1];
  var _useLazyService = useLazyService(request, controller, {
      ErrorHandler: LegionError,
      initialState: initialState
    }),
    _useLazyService2 = _slicedToArray(_useLazyService, 2),
    getPermissions = _useLazyService2[0],
    _useLazyService2$ = _useLazyService2[1],
    loading = _useLazyService2$.loading,
    error = _useLazyService2$.error,
    data = _useLazyService2$.data;
  var isLoadingAnywhere = state.isLoading || loading;
  var hasLoadedAnywhere = state.error || state.hasLoaded || !!data || !!error;
  var willSendRequest = orgId && enabled && !hasLoadedAnywhere && !isLoadingAnywhere;
  var analyticAttributes = useMemo(function () {
    return {
      userId: attributes === null || attributes === void 0 ? void 0 : attributes.userId,
      orgId: orgId,
      cloudId: cloudId
    };
  }, [attributes === null || attributes === void 0 ? void 0 : attributes.userId, orgId, cloudId]);
  useEffect(function () {
    var fetchData = /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (!willSendRequest) {
                _context.next = 5;
                break;
              }
              setLoading(true);
              fireOperationalEvent(createAnalyticsEvent, {
                action: 'fetched',
                actionSubject: 'use-team-permissions',
                attributes: analyticAttributes
              });
              _context.next = 5;
              return getPermissions();
            case 5:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function fetchData() {
        return _ref3.apply(this, arguments);
      };
    }();
    fetchData();
    return function () {
      setLoading(false);
    };
  }, [getPermissions, setLoading, willSendRequest, createAnalyticsEvent, orgId, cloudId, analyticAttributes]);
  useEffect(function () {
    if (data !== null && data !== void 0 && data.permissions) {
      setPermissions(data.permissions);
    }
    if (error) {
      setError(error);
    }
  }, [data, error, setPermissions, setError]);
  return _objectSpread({
    loading: loading,
    data: data
  }, state);
};
export var useTeamsPermissionsServiceNext = function useTeamsPermissionsServiceNext(scope) {
  var _ref4 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      enabled: false,
      attributes: {}
    },
    enabled = _ref4.enabled,
    attributes = _ref4.attributes;
  var _useAnalyticsEvents2 = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents2.createAnalyticsEvent;

  // backwards compatibility allow orgId to be passed as a string
  var _ref5 = typeof scope === 'string' || !scope ? {
      orgId: scope,
      cloudId: undefined
    } : scope,
    orgId = _ref5.orgId,
    cloudId = _ref5.cloudId;
  var _useTeamsPermissionsS4 = useTeamsPermissionsStore(),
    _useTeamsPermissionsS5 = _slicedToArray(_useTeamsPermissionsS4, 2),
    state = _useTeamsPermissionsS5[0],
    getPermissions = _useTeamsPermissionsS5[1].getPermissions;
  var fetchData = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          getPermissions({
            orgId: orgId,
            cloudId: cloudId,
            enabled: enabled
          }, {
            onFetch: function onFetch() {
              return fireOperationalEvent(createAnalyticsEvent, {
                action: 'fetched',
                actionSubject: 'use-team-permissions',
                attributes: {
                  userId: attributes === null || attributes === void 0 ? void 0 : attributes.userId,
                  orgId: orgId,
                  cloudId: cloudId
                }
              });
            }
          });
        case 1:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  })), [orgId, cloudId, enabled, getPermissions, createAnalyticsEvent, attributes === null || attributes === void 0 ? void 0 : attributes.userId]);
  useEffect(function () {
    fetchData();
  }, [fetchData]);
  return _objectSpread({
    loading: state.isLoading,
    data: state.permissionsResponse,
    error: state.error
  }, state);
};
export var useTeamsPermissionsService = fg('pf-directory-settings-query-migration') ? useTeamsPermissionsServiceNext : useTeamsPermissionsServiceLegacy;